import { FeaturedIconTile } from "@/shared/ui/featured-icon";
import { EntityNotFoundError } from "@/shared/lib/errors/404-not-found-error";
import { motion } from "motion/react";
import { AnnoyedIcon, ArrowLeftIcon } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { H3, Text } from "@/shared/ui/typography";
import { useNavigate } from "@tanstack/react-router";
import { T, useTranslation } from "@/shared/i18n";

type Props = {
  error?: Error | EntityNotFoundError;
  statusCode?: number;
};

// Untitled UI status pattern (same as EmptyState): a featured-icon tile, then
// title + supporting text, then actions.
export const ErrorPage = ({ error, statusCode }: Props = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const errorMessage = error?.message || t("The requested page was not found");

  return (
    <motion.div
      className="mx-auto flex max-w-xl flex-1 flex-col items-center justify-center p-6 text-center"
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      <FeaturedIconTile>
        <AnnoyedIcon />
      </FeaturedIconTile>

      <div className="mt-4 flex flex-col gap-1">
        <H3 className="text-lg font-semibold leading-7 text-neutral-900">
          {statusCode ?? <T id="Something went wrong" />}
        </H3>
        <Text className="max-w-sm text-neutral-600">{errorMessage}</Text>
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <Button onClick={() => navigate({ to: "/" })} variant="outline">
          <T id="Go to home" />
        </Button>
        <Button onClick={() => window.history.back()}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T id="Go back" />
        </Button>
      </div>
    </motion.div>
  );
};
